import Swal from 'sweetalert2';
import { type EconomyScoreState, type EconomyScoreFactors, stateConfig, getEconomyScoreState } from '@/mocks/radioEconomyScores';

/* ── Factor labels ── */
const factorLabels: Record<keyof EconomyScoreFactors, { label: string; icon: string }> = {
  seguidores:    { label: 'Seguidores',    icon: 'ri-user-heart-line' },
  oyentes:       { label: 'Oyentes',       icon: 'ri-headphone-line' },
  interaccion:   { label: 'Interacción',   icon: 'ri-heart-line' },
  patrocinadores:{ label: 'Patrocinadores',icon: 'ri-hand-heart-line' },
  campañas:      { label: 'Campañas',      icon: 'ri-megaphone-line' },
  eventos:       { label: 'Eventos',       icon: 'ri-calendar-event-line' },
};

/* ── Size maps ── */
const sizeMap = {
  xs: { container: 'w-[44px] h-[44px]', viewBox: 44, r: 18, stroke: 4, score: 'text-xs', label: 'text-[7px]' },
  sm: { container: 'w-[56px] h-[56px]', viewBox: 56, r: 22, stroke: 5, score: 'text-sm', label: 'text-[8px]' },
  md: { container: 'w-[80px] h-[80px]', viewBox: 80, r: 33, stroke: 6, score: 'text-xl', label: 'text-[9px]' },
  lg: { container: 'w-[110px] h-[110px]', viewBox: 110, r: 46, stroke: 8, score: 'text-2xl', label: 'text-[10px]' },
};

/* ── Color hex helper ── */
function getHex(cssClass: string): string {
  const map: Record<string, string> = {
    'emerald-500': '#10b981', 'emerald-100': '#d1fae5',
    'green-500': '#22c55e', 'green-100': '#dcfce7',
    'amber-500': '#f59e0b', 'amber-100': '#fef3c7',
    'orange-500': '#f97316', 'orange-100': '#ffedd5',
    'red-500': '#ef4444', 'red-100': '#fee2e2',
  };
  return map[cssClass] || '#6b7280';
}

/* ── Props ── */
interface RadioEconomyScoreProps {
  score: number;
  factors: EconomyScoreFactors;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  clickable?: boolean;
  radioName?: string;
}

export default function RadioEconomyScore({
  score, factors, size = 'md', showLabel = true, clickable = true, radioName,
}: RadioEconomyScoreProps) {
  const state = getEconomyScoreState(score);
  const config = stateConfig[state];
  const sm = sizeMap[size];
  const circumference = 2 * Math.PI * sm.r;
  const dashOffset = circumference - (score / 100) * circumference;

  const handleClick = async () => {
    if (!clickable) return;

    const factorEntries = Object.entries(factors) as [keyof EconomyScoreFactors, number][];
    const factorRows = factorEntries
      .map(([key, val]) => {
        const fl = factorLabels[key];
        const fState = getEconomyScoreState(val);
        const fConfig = stateConfig[fState];
        const hex = getHex(fConfig.ring.replace('text-', ''));
        return `
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
            <div style="width:28px;height:28px;border-radius:8px;background:${hex}15;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
              <i class="${fl.icon}" style="color:${hex};font-size:13px;"></i>
            </div>
            <div style="flex:1;min-width:0;">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:3px;">
                <span style="font-size:12px;color:#374151;font-weight:500;">${fl.label}</span>
                <span style="font-size:12px;font-weight:700;color:${hex};">${val}</span>
              </div>
              <div style="height:4px;background:#f3f4f6;border-radius:2px;overflow:hidden;">
                <div style="height:100%;width:${val}%;background:${hex};border-radius:2px;transition:width 0.6s ease;"></div>
              </div>
            </div>
          </div>
        `;
      })
      .join('');

    await Swal.fire({
      title: radioName ? `Economy Score · ${radioName}` : 'Economy Score',
      html: `
        <div style="text-align:left;font-size:13px;">
          <div style="text-align:center;margin-bottom:14px;">
            <div style="display:inline-flex;align-items:center;gap:8px;padding:6px 14px;background:${getHex(config.ring.replace('text-', ''))}15;border-radius:12px;">
              <span style="font-size:28px;font-weight:800;color:${getHex(config.ring.replace('text-', ''))};">${score}</span>
              <span style="display:flex;align-items:center;gap:4px;font-size:13px;font-weight:700;color:${getHex(config.ring.replace('text-', ''))};">
                <span style="width:8px;height:8px;border-radius:50%;background:${getHex(config.dot.replace('bg-', ''))};display:inline-block;"></span>
                ${config.label}
              </span>
            </div>
          </div>
          <p style="margin-bottom:14px;color:#6b7280;font-size:11px;line-height:1.5;">
            El Economy Score mide el <strong style="color:#374151;">potencial comercial</strong> de la radio evaluando 6 factores: seguidores, oyentes, interacción, patrocinadores, campañas activas y eventos. Es un indicador clave para detectar radios con alta capacidad de monetización.
          </p>
          ${factorRows}
        </div>
      `,
      confirmButtonText: 'Cerrar',
      confirmButtonColor: '#1f2937',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold text-sm',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    });
  };

  return (
    <div
      className={`${sm.container} relative shrink-0 ${clickable ? 'cursor-pointer group' : ''}`}
      onClick={handleClick}
      title={`Economy Score: ${score} · ${config.label} — Potencial Comercial`}
    >
      <svg className="w-full h-full -rotate-90" viewBox={`0 0 ${sm.viewBox} ${sm.viewBox}`}>
        <circle
          cx={sm.viewBox / 2} cy={sm.viewBox / 2} r={sm.r}
          fill="none" stroke="currentColor"
          className={config.track}
          strokeWidth={sm.stroke}
        />
        <circle
          cx={sm.viewBox / 2} cy={sm.viewBox / 2} r={sm.r}
          fill="none" stroke="currentColor"
          className={`${config.ring} transition-all duration-700 ease-out`}
          strokeWidth={sm.stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`${sm.score} font-extrabold ${config.color} leading-none`}>{score}</span>
        {showLabel && (
          <span className={`${sm.label} ${config.color} font-bold mt-0.5 whitespace-nowrap`}>{config.label}</span>
        )}
      </div>
    </div>
  );
}